import { ERROR_CODES } from '@ibe/errors';

import { ApiError, ContractError, NetworkError } from './errors.js';

/**
 * What a failure says, and where it is shown.
 *
 * Both decisions live here so they are made once. The portal previously
 * reimplemented a `messageFor()` per page, which is how the same 403 came to
 * read three different ways on three screens.
 */

const UNPROCESSABLE = 422;
const CLIENT_FLOOR = 400;
const SERVER_FLOOR = 500;

export const ERROR_CLASS = {
  /** Belongs on a form field. The one class that never toasts. */
  FIELD: 'FIELD',
  /** The server's considered answer. Toast, and render in place if the page cannot load. */
  OPERATIONAL: 'OPERATIONAL',
  /** No answer arrived. Toast, and offer a retry in place. */
  TRANSPORT: 'TRANSPORT',
  /** Our bug. Toast with the correlation id, boundary in place, logged. */
  UNEXPECTED: 'UNEXPECTED',
} as const;

export type ErrorClass = (typeof ERROR_CLASS)[keyof typeof ERROR_CLASS];

/**
 * User-facing copy, keyed by the platform's error registry.
 *
 * Only codes whose server message is too terse for an operator need an entry.
 * An unregistered code falls through to the server's own message, which
 * `packages/errors` guarantees is safe to display — so this map stays small
 * rather than duplicating the whole registry.
 */
const COPY: Readonly<Record<string, string>> = {
  [ERROR_CODES.UNAUTHORIZED]: 'Your session has ended. Sign in again to continue.',
  [ERROR_CODES.INSUFFICIENT_PERMISSION]:
    'You do not have permission to do this. Ask an administrator if you need access.',
  [ERROR_CODES.ACCESS_DENIED]: 'You do not have access to this record.',
  [ERROR_CODES.FORBIDDEN]: 'You do not have permission to do this.',
  [ERROR_CODES.NOT_FOUND]: 'That record no longer exists. It may have been deleted.',
  [ERROR_CODES.CONFLICT]: 'Someone else changed this while you were editing. Reload and try again.',
  [ERROR_CODES.RATE_LIMIT_EXCEEDED]: 'Too many requests. Wait a moment and try again.',
  [ERROR_CODES.SERVICE_UNAVAILABLE]:
    'That service is temporarily unavailable. Try again in a moment.',
  [ERROR_CODES.IDEMPOTENCY_CONFLICT]:
    'This request was already submitted with different details. Reload and try again.',
};

const GENERIC = 'Something went wrong. Try again, and quote the reference below if it persists.';

export function messageForError(error: unknown): string {
  // Checked before ApiError only because it is not one; order is otherwise
  // irrelevant. Contract detail is deliberately withheld — it names internal
  // paths and is meaningless to whoever is reading the screen.
  if (error instanceof ContractError) return GENERIC;
  if (error instanceof NetworkError) return error.message;

  if (error instanceof ApiError) {
    return COPY[error.code] ?? (error.message === '' ? GENERIC : error.message);
  }

  return GENERIC;
}

/**
 * Which of the four treatments a failure gets.
 *
 * Every class except `FIELD` is announced by a toast, because a failure is
 * frequently invisible otherwise: a background refetch that 403s or times out
 * leaves the screen showing the rows it already had, and the in-place state
 * only renders when there is no data to display.
 *
 * A field error is the exclusion. It belongs beside the input that caused it;
 * in a toast it leaves the field looking valid and disappears before the
 * reader has worked out which of four inputs to fix.
 *
 * The classification still matters beyond the toast, because it also decides
 * what a screen renders in the space where the data would have been —
 * `NotPermitted`, a retry, or an error boundary.
 */
export function classifyError(error: unknown): ErrorClass {
  if (error instanceof ContractError) return ERROR_CLASS.UNEXPECTED;
  if (error instanceof NetworkError) return ERROR_CLASS.TRANSPORT;

  if (error instanceof ApiError) {
    // A validation failure with no field to attach to has nowhere to render,
    // so it is treated as a page-level condition instead of vanishing.
    if (error.status === UNPROCESSABLE && Object.keys(error.fieldErrors).length > 0) {
      return ERROR_CLASS.FIELD;
    }
    if (error.status >= CLIENT_FLOOR && error.status < SERVER_FLOOR) {
      return ERROR_CLASS.OPERATIONAL;
    }
    return ERROR_CLASS.UNEXPECTED;
  }

  return ERROR_CLASS.UNEXPECTED;
}

/**
 * The text an error surface shows, correlation id included.
 *
 * **Defined once, here.** An earlier draft of this plan had it inlined in both
 * `resource-list-page.tsx` and the property detail screen — two copies that
 * would have drifted the first time one of them was adjusted, and exactly the
 * duplication `messageFor()`-per-page already demonstrated.
 *
 * Returns `undefined` for no error so a caller can pass the result straight to
 * a component's optional `error` prop without a branch.
 */
export function errorTextFor(error: unknown, fallback?: string): string | undefined {
  if (error === null || error === undefined) return undefined;

  // A throw the platform did not produce — a malformed body, a bug in a
  // handler — has no registry entry and no safe server message. A screen that
  // knows what it was loading can say something more useful than the generic,
  // so it gets to supply that; the generic is only for screens that cannot.
  if (!(error instanceof ApiError) && !(error instanceof NetworkError)) {
    return fallback ?? GENERIC;
  }

  const message = messageForError(error);

  // Appended only when there is one to append. A proxy or load balancer that
  // answered instead of a service carries no correlation id, and
  // "(reference )" is worse than no reference.
  if (error instanceof ApiError && error.correlationId !== '') {
    return `${message} (reference ${error.correlationId})`;
  }

  return message;
}
